import { MapLike } from '@iotv/iotv-v3-types'
import { Milliseconds } from '../../../../src/constants'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../lambda'
import { getOverlapLength } from '../../math'
import { TimeBucket, TimeBucketRequest, TimeBucketType, TimeBucketUnits } from './types'
import { getTimeBucketEnd, getTimeBucketStartDateTimeForTimestamp } from './getTimeBucketStartDateTimeForTimestamp'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

export const isInTimeBucket = (timestampMs: number, timeBucket: TimeBucket) => {
  const [startTimestamp, endTimestamp] = timeBucket
  return startTimestamp <= timestampMs && endTimestamp > timestampMs
}

/**
 * @function getCurrentTimeBucket returns the currentTimeBucket for single timestamp
 * @param param0
 * @returns
 */

export const getCurrentTimeBucket = (timeBucketDef: TimeBucketType, timestamp: number): TimeBucket => {
  const currentBuckets = getTimeBuckets({ timeBucketDef, startTimestamp: timestamp, endTimestamp: timestamp })
  return currentBuckets[0]
}

/**
 * @function getTimeBuckets returns the time buckets which cover the time range based in the definition
 * @param TimeBucketRequest
 * @returns TimeBucket[]
 */
export const getTimeBuckets = ({ timeBucketDef, startTimestamp, endTimestamp }: TimeBucketRequest) => {
  const buckets: TimeBucket[] = []
  const iterationDate = getTimeBucketStartDateTimeForTimestamp({ timeBucketDef, timestamp: startTimestamp })

  let bucketStart = iterationDate.valueOf()
  do {
    const bucketEnd = getTimeBucketEnd({ timeBucketDef, timeBucketStartTimestamp: bucketStart })
    const timeBucket: TimeBucket = [bucketStart, bucketEnd]
    buckets.push(timeBucket)
    bucketStart = bucketEnd
  } while (bucketStart <= endTimestamp)

  return buckets
}

export type GetApportionToTimeBucketsMapRequest = { timeBucketDef: TimeBucketType; measureRange: { fromTimestamp: number; toTimestamp: number } }
export type GetApportionToTimeBucketMapResponse = {
  bucketIndex: number
  bucket: TimeBucket
  overlap: number
  apportionment: number
}

/**
 * @function getApportionToTimeBucketsMap return the time buckets covering a range and returns the buckets plus the proportion of a value that should be put in each bucket
 * @param GetApportionToTimeBucketsMapRequest
 * @returns GetApportionToTimeBucketMapResponse
 */

export const getApportionToTimeBucketsMap = ({ timeBucketDef, measureRange }: GetApportionToTimeBucketsMapRequest): GetApportionToTimeBucketMapResponse[] => {
  const buckets: TimeBucket[] = getTimeBuckets({ timeBucketDef, startTimestamp: measureRange.fromTimestamp, endTimestamp: measureRange.toTimestamp })
  const meaureLength = measureRange.toTimestamp - measureRange.fromTimestamp

  const apportionedRes = buckets
    .map((bucket, i) => {
      const overlap = getOverlapLength([bucket, [measureRange.fromTimestamp, measureRange.toTimestamp]])

      debug && (isNaN(overlap) || overlap === 0) && console.log(`Zero overlap with`, { bucket, measureRange })

      if (overlap == 0) return null

      const apportionment = overlap / meaureLength

      debug && console.log('getApportionToTimeBucketsMap', { overlap, apportionment, measureRange })

      return {
        bucketIndex: i,
        bucket,
        overlap,
        apportionment,
      }
    })
    .filter((b) => b != null)
  return apportionedRes
}
export type MeasureValueRange = { fromTimestamp: number; toTimestamp: number; value: number }
export type ApportionMeasureValueRequest = { timeBucketDef: TimeBucketType; measureValueRange: MeasureValueRange }
export type ApportionMeasureResponse = (GetApportionToTimeBucketMapResponse & { apportionedValue: number })[]

/**
 * @functin apportionMeasure returns the proportion of a value to be added to overlapping time buckets
 * @param param0
 * @returns
 */
export const apportionMeasureValue = ({ timeBucketDef, measureValueRange }: ApportionMeasureValueRequest): ApportionMeasureResponse => {
  const apportionedFactorRes = getApportionToTimeBucketsMap({ timeBucketDef, measureRange: measureValueRange })

  const apportionedRes: ApportionMeasureResponse = apportionedFactorRes.map((bucketRes) => ({
    ...bucketRes,
    apportionedValue: bucketRes.apportionment * measureValueRange.value,
  }))

  return apportionedRes
}

export type MeasureValueMapRange = { fromTimestamp: number; toTimestamp: number; valueMap: MapLike<number | null> }
export type ApportionMeasureValueMapRequest = { timeBucketDef: TimeBucketType; measureValueMapRange: MeasureValueMapRange }
export type ApportionMeasureMapResponse = (GetApportionToTimeBucketMapResponse & { apportionedValuesMap: MapLike<number | null> })[]

/**
 * @functin apportionMeasure returns the proportion of a value to be added to overlapping time buckets
 * @param param0
 * @returns
 */
export const apportionMeasureValuesMap = ({ timeBucketDef, measureValueMapRange }: ApportionMeasureValueMapRequest): ApportionMeasureMapResponse => {
  const apportionedFactorRes = getApportionToTimeBucketsMap({ timeBucketDef, measureRange: measureValueMapRange })

  const apportionedRes: ApportionMeasureMapResponse = apportionedFactorRes.map((bucketRes) => ({
    ...bucketRes,
    apportionedValuesMap: getApportionedValuesMap(bucketRes, measureValueMapRange.valueMap),
  }))

  return apportionedRes
}

const getApportionedValuesMap = (bucketRes: GetApportionToTimeBucketMapResponse, valuesMap: MapLike<number | null>): MapLike<number | null> => {
  const apportionedValuesMap: MapLike<number | null> = Object.fromEntries(
    Object.entries(valuesMap).map(([k, v]) => [k, v && typeof v === 'number' && !isNaN(v) ? bucketRes.apportionment * v : null]),
  )
  return apportionedValuesMap
}

/**
 * @function coversRange determines whether an array of timestamps with first being beginning of period, last end, fully spans a time bucket, with no one sub period being fully outside bucket range
 * This most likely applies to updating Normalized CMV which needs one hit
 * @param bucket
 * @param timestamps
 * @returns
 */
export const coversRange = (bucket: TimeBucket, timestamps: number[]): boolean => {
  const isAscending = timestamps.every((t, i) => t >= (timestamps[i - 1] ?? 0))
  if (!isAscending) {
    console.log('WARN: Not ordered returing false', timestamps)
    return false
  } else {
    const [fromTimestamp, ...evaluatableTimestamps] = timestamps
    const endTimestamp = timestamps[timestamps.length - 1]
    /**
     * @returns note timestamps[ i ] because it is one before evaluatableTimestamps[ i ]
     */
    return (
      evaluatableTimestamps.every(
        (timestamp, i) =>
          getOverlapLength([bucket, [timestamps[i] ?? -Infinity, timestamp]]) > 0 || timestamps[i] === timestamp, // otherwise repeated timestamps give zero overlap
      ) &&
      fromTimestamp <= bucket[0] &&
      endTimestamp >= bucket[1]
    )
  }
}

export const comparableProductOf = (args: number[], rx: number[]) =>
  args.reduce((a, c, i) => {
    const multipliers = rx.slice(0, args.length - i)
    return a + c * multipliers.reduce((a, c) => a * c)
  }, 0)
